import * as React from "react"
import { connect } from "react-redux"
import { RootState } from "../../../../store"
import { FC, useEffect, useState } from "react"
import { UserExpenseForm, Affiliate, AdminUserModalsContainerProps } from "../../../../types"
import {
  fetchUserExpenseData,
  paymentMethodsAdminEntry,
  saveUserExpenseData,
  selectUser,
  fetchAffiliateList,
  applyUserExpenseToPreviousMonths,
  updateUserHasUpdateCount,
  updateUserUnverifiedContractCount,
  updateUserPaymentMethodUpdatedCount
} from "../../../../actions/adminActions"
import { AdminUserModalOptions, errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import AdminPaymentMethodModal from "./modals/AdminPaymentMethodModal"
import AdminAffiliateFromUserModal from "./modals/AdminAffiliateFromUserModal"
import AdminAffiliateToUserModal from "./modals/AdminAffiliateToUserModal"
import { toast } from "react-toastify"
import { affiliate, users } from "../../../../apis"
import { mapListToSnakeCase } from "../../../../utility"
import { fetchUserData } from "../../../../actions"
import AdminUserCreditModal from "./modals/AdminUserCreditModal"
import AdminNotificationSendModal from "./modals/AdminNotificationSendModal"
import AdminUserAdvanceModal from "./modals/AdminUserAdvanceModal"
import AdminUserExpenseModal from "./modals/AdminUserExpenseModal"
import ReloadModal from "./modals/ReloadModal"
import AdminCurrencySelectionModal from "./modals/AdminCurrencySelectionModal"
import AdminNewUserTakedownModal from "./modals/AdminNewUserTakedownModal"
import AdminPermissionModal from "./modals/AdminPermissionModal"
import AdminUserContractModal from "./modals/AdminUserContractModal"
import AdminAgreementsPayoutModal from "./modals/AdminAgreementsPayoutModal"
import ConfirmationModal from "../admin-catalog/modals/ConfirmationModal"

const AdminUserModalsContainer: FC<AdminUserModalsContainerProps> = (props) => {
  const { modalOption, setModalOption } = props

  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    const userExpenseList = [AdminUserModalOptions.USER_EXPENSE, AdminUserModalOptions.AGREEMENTS_AND_PAYOUTS]
    if (modalOption && userExpenseList.includes(modalOption) && props.user?.id) {
      props.fetchUserExpenseData(props.user?.id)
    }
  }, [modalOption])

  useEffect(() => {
    if (props.user?.id) {
      props.fetchAffiliateList(props.user.id)
    }
  }, [props.user?.id])

  const onClose = () => {
    setModalOption(undefined)
    //props.selectUser(null)
  }

  const [openCurrencySelection, setOpenCurrencySelection] = useState<boolean>(false)
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | undefined>(undefined)

  const saveAffiliate = async (affiliateList: Affiliate[]) => {
    if (!props.user?.id) {
      return
    }

    try {
      const response = await affiliate(`/`, "post", {
        from_user_id: props.user.id,
        affiliate_list: mapListToSnakeCase(affiliateList)
      })
      if (response.status === 200) {
        toast.success("Affiliate saved successfully", successFilledToastOptions)
        setModalOption(undefined)
        props.fetchAffiliateList(props.user.id)
      } else {
        toast.error("Error saving affiliate", errorFilledToastOptions)
      }
    } catch (err) {
      toast.error("Error saving affiliate", errorFilledToastOptions)
    }
  }

  const onCreateAdvanceAccount = async () => {
    if (!props.user?.id) return
    setLoading(true)
    try {
      const response = await users(`/create_advance_account/`, "post", { user_id: props.user.id })
      onClose()
      setLoading(false)
      if (response.data.success) {
        toast.success(
          "Advance account created successfully. Refresh the page to see the result",
          successFilledToastOptions
        )
      } else {
        toast.error(response.data.message || "Error creating advance account", {
          ...errorFilledToastOptions,
          autoClose: false
        })
      }
    } catch (err) {
      setLoading(false)
      toast.error("Error creating advance account", errorFilledToastOptions)
    }
  }

  const onMergeAdvanceAccount = async () => {
    if (!props.user?.id) return
    setLoading(true)
    try {
      const response = await users(`/merge_advance_account/`, "post", { user_id: props.user.id })
      setLoading(false)
      onClose()
      if (response.data.success) {
        toast.success(
          "Advance account merged successfully. Refresh the page to see the result",
          successFilledToastOptions
        )
      } else {
        toast.error(response.data.message || "Error merging advance account", {
          ...errorFilledToastOptions,
          autoClose: false
        })
      }
    } catch (err) {
      setLoading(false)
      toast.error("Error merging advance account", errorFilledToastOptions)
    }
  }

  const onUserExpenseFormSubmit = async (values: UserExpenseForm) => {
    props.saveUserExpenseData(values)
  }

  const onUserExpenseApplyPreviousMonths = async (userExpenseId?: number) => {
    if (!userExpenseId) return

    const months = window.prompt(
      "Enter the months (YYYYMM) to which you want to apply the changes, separated by commas. Leave empty to apply to all previous months."
    )

    if (!months && months !== "") return

    if (
      !window.confirm(
        "Are you sure? It will re-calculate Artist Net from Gross! If total_net is manually entered, changes will be removed and re-calculated."
      )
    ) {
      return
    }

    props.applyUserExpenseToPreviousMonths(userExpenseId, months)
    onClose()
  }

  if (!props.user) return null

  return (
    <div>
      <ReloadModal
        isOpen={modalOption === AdminUserModalOptions.RELOAD}
        handleClose={onClose}
        refreshSummaryForUser={true}
      />
      <AdminPaymentMethodModal
        open={modalOption === AdminUserModalOptions.PAYMENT_METHODS}
        onClose={onClose}
        paymentMethodsAdminEntry={props.paymentMethodsAdminEntry}
        userId={props.user?.id}
        readOnly={false}
        isModal={true}
      />
      <AdminUserCreditModal
        open={modalOption === AdminUserModalOptions.USER_CREDITS}
        onClose={onClose}
        user={props?.user}
        isModal={true}
      />
      <AdminUserAdvanceModal
        open={modalOption === AdminUserModalOptions.USER_ADVANCE}
        onClose={onClose}
        userId={props.user?.id}
        user={props?.user}
        loggedInUser={props.loggedInUser}
        setSelectedCurrencyId={setSelectedCurrencyId}
        onChangeCurrencyClick={() => {
          setOpenCurrencySelection(true)
          setModalOption(undefined)
        }}
        isModal={true}
      />
      <AdminPermissionModal
        open={modalOption === AdminUserModalOptions.ADMIN_PERMISSION}
        onClose={onClose}
        user={props.user}
      />
      <AdminCurrencySelectionModal
        open={openCurrencySelection}
        userId={props.user?.id}
        onClose={() => {
          setOpenCurrencySelection(false)
          setModalOption(AdminUserModalOptions.USER_ADVANCE)
        }}
        user={props?.user}
        selectedCurrencyId={selectedCurrencyId}
      />
      <AdminAffiliateFromUserModal
        open={modalOption === AdminUserModalOptions.AFFILIATE_FROM_LIST}
        onClose={onClose}
        user={props?.user}
        saveAffiliate={saveAffiliate}
        affiliateList={props.affiliateFromList}
      />
      <AdminAffiliateToUserModal
        open={modalOption === AdminUserModalOptions.AFFILIATE_TO_LIST}
        onClose={onClose}
        user={props?.user}
        affiliateList={props.affiliateToList}
      />
      <AdminNotificationSendModal
        open={modalOption === AdminUserModalOptions.SEND_NOTIFICATION}
        onClose={onClose}
        user={props.user}
      />
      <AdminUserExpenseModal
        open={modalOption === AdminUserModalOptions.USER_EXPENSE}
        onClose={onClose}
        selectedUserExpense={props.selectedUserExpense}
        onUserExpenseFormSubmit={onUserExpenseFormSubmit}
        user={props.user}
        loggedInUser={props.loggedInUser}
        onApplyPreviousMonths={onUserExpenseApplyPreviousMonths}
        isModal={true}
      />
      <AdminNewUserTakedownModal
        open={modalOption === AdminUserModalOptions.USER_TAKEDOWN}
        onClose={onClose}
        user={props.user}
      />
      <AdminUserContractModal
        open={modalOption === AdminUserModalOptions.USER_CONTRACT}
        onClose={onClose}
        userId={props.user?.id}
        isModal={true}
      />
      <ConfirmationModal
        isOpen={modalOption === AdminUserModalOptions.CREATE_ADVANCE_ACCOUNT}
        handleClose={onClose}
        disabled={loading}
        handleConfirm={onCreateAdvanceAccount}
        description={`This action cannot be undone! Are you sure you want to create the advance account for ${props.user.firstName} ${props.user.lastName}?`}
      />
      <ConfirmationModal
        isOpen={modalOption === AdminUserModalOptions.MERGE_ADVANCE_ACCOUNT}
        handleClose={onClose}
        disabled={loading}
        handleConfirm={onMergeAdvanceAccount}
        description={`This action cannot be undone! Are you sure you want to merge the advance account for ${props.user.firstName} ${props.user.lastName}?`}
      />
      <AdminAgreementsPayoutModal
        open={modalOption === AdminUserModalOptions.AGREEMENTS_AND_PAYOUTS}
        paymentMethodsAdminEntry={props.paymentMethodsAdminEntry}
        onClose={onClose}
        user={props.user}
        onUserExpenseFormSubmit={onUserExpenseFormSubmit}
        loggedInUser={props.loggedInUser}
        selectedUserExpense={props.selectedUserExpense}
        isModal={true}
        userHasUpdateCount={props.userHasUpdateCount}
        updateUserHasUpdateCount={props.updateUserHasUpdateCount}
        userUnverifiedContractCount={props.userUnverifiedContractCount}
        updateUserUnverifiedContractCount={props.updateUserUnverifiedContractCount}
        userPaymentMethodUpdatedCount={props.userPaymentMethodUpdatedCount}
        updateUserPaymentMethodUpdatedCount={props.updateUserPaymentMethodUpdatedCount}
      />
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.admin.selectedUser,
    loggedInUser: state.auth.user,
    selectedUserExpense: state.admin.selectedUserExpense,
    affiliateFromList: state.admin.affiliateFromList,
    affiliateToList: state.admin.affiliateToList,
    userHasUpdateCount: state.admin.userHasUpdateCount,
    userUnverifiedContractCount: state.admin.userUnverifiedContractCount,
    userPaymentMethodUpdatedCount: state.admin.userPaymentMethodUpdatedCount
  }
}

export default connect(mapStateToProps, {
  selectUser,
  fetchUserExpenseData,
  saveUserExpenseData,
  paymentMethodsAdminEntry,
  fetchAffiliateList,
  fetchUserData,
  applyUserExpenseToPreviousMonths,
  updateUserHasUpdateCount,
  updateUserUnverifiedContractCount,
  updateUserPaymentMethodUpdatedCount
})(AdminUserModalsContainer)
